import { z } from 'zod'

import { NoticeStatus, noticeEventSchema } from './defectNotice'
import { fileCompleteResponseSchema, validationErrors } from '.'

export const noticeReporterSchema = z.object({
  phoneNumber: z.string(),
  email: z.string().email(),
})

const feedbackQuestionDescriptionFieldsSchema = z
  .object({
    title: z.string().max(100).nullable(),
    description: z.string().nullable(),
    fileUuids: z.array(z.string().uuid()),
  })
  .superRefine(({ title, description, fileUuids }, ctx) => {
    // At least one of the following fields can't be null
    if (
      title === null &&
      description === null &&
      (!fileUuids || fileUuids.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: validationErrors.title_description_or_files_required,
        path: ['description'],
      })
    }
  })

export const createFeedbackQuestionRequestSchema = z
  .object({
    contractUuid: z.string().uuid(),
    defectClassId: z.number().int().nullable(),
    noticeReporter: noticeReporterSchema.nullable(),
  })
  .and(feedbackQuestionDescriptionFieldsSchema)

export type CreateFeedbackQuestion = z.infer<
  typeof createFeedbackQuestionRequestSchema
>

export const feedbackAndQuestionNoticeResponseSchema = z.object({
  id: z.number(),
})

export type FeedbackAndQuestionNoticeResponse = z.infer<
  typeof feedbackAndQuestionNoticeResponseSchema
>

export const feedbackAndQuestionNoticeCreateSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  fileUuids: z.array(z.string().uuid()),
  contractUuid: z.string().uuid(),
  noticeReporter: z
    .object({
      phoneNumber: z.string(),
      email: z.string().email().nullable(),
      description: z.string().optional(),
    })
    .nullable(),
  expectsResponse: z.boolean().optional(),
})

export type FeedbackAndQuestionNoticeCreate = z.infer<
  typeof feedbackAndQuestionNoticeCreateSchema
>

export const feedbackAndQuestionNoticeListItemSchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  defectClassId: z.number().int().nullable(),
  latestEvent: noticeEventSchema,
  status: z.nativeEnum(NoticeStatus),
  noticeType: z.enum(['feedback', 'question']),
})

export const feedbackAndQuestionNoticeListSchema = z.array(
  feedbackAndQuestionNoticeListItemSchema
)

export type FeedbackAndQuestionNoticeListItem = z.infer<
  typeof feedbackAndQuestionNoticeListItemSchema
>

const fileCompleteResponseWithUrlSchema = fileCompleteResponseSchema.extend({
  url: z.string(),
})

export const feedbackAndQuestionNoticeDetailsSchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  defectClassId: z.number().int().nullable(),
  files: z.array(fileCompleteResponseWithUrlSchema).optional(),
  contractUuid: z.string().uuid(),
  noticeReporter: z
    .object({
      phoneNumber: z.string(),
      email: z.string().email().nullable(),
      description: z.string().optional(),
    })
    .nullable(),
  status: z.nativeEnum(NoticeStatus),
  events: z.array(noticeEventSchema).optional(),
  expectsResponse: z.boolean().optional(),
})

export type FeedbackAndQuestionNoticeDetails = z.infer<
  typeof feedbackAndQuestionNoticeDetailsSchema
>
export type FeedbackAndQuestionNoticeEvent = z.infer<typeof noticeEventSchema>
